// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { App, Button, Empty, List, Popconfirm, Segmented, Space, Table, Tag, Typography } from "antd";
import { Homeserver } from "../api";
import type { AdminAccount, AdminReport, AdminRoom, Session } from "../types";

const hs = new Homeserver();

// AdminConsole is the server-admin moderation view: an abuse-report queue, the
// account directory (deactivate/reactivate), and a room overview. Admin-only — the
// caller (Profile) renders it only when whoami.admin is true; the API also gates.
export function AdminConsole({
  session,
  onOpenRoom,
  onDiscuss,
}: {
  readonly session: Session;
  readonly onOpenRoom?: (roomID: string) => void;
  readonly onDiscuss?: (reportID: string, reporter: string, room?: string) => void;
}) {
  const [tab, setTab] = useState<"Reports" | "Accounts" | "Rooms">("Reports");
  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Segmented options={["Reports", "Accounts", "Rooms"]} value={tab} onChange={(v) => setTab(v as typeof tab)} block />
      {tab === "Reports" && <ReportsTab session={session} onOpenRoom={onOpenRoom} onDiscuss={onDiscuss} />}
      {tab === "Accounts" && <AccountsTab session={session} />}
      {tab === "Rooms" && <RoomsTab session={session} onOpenRoom={onOpenRoom} />}
    </Space>
  );
}

function ReportsTab({
  session,
  onOpenRoom,
  onDiscuss,
}: {
  readonly session: Session;
  readonly onOpenRoom?: (roomID: string) => void;
  readonly onDiscuss?: (reportID: string, reporter: string, room?: string) => void;
}) {
  const { message } = App.useApp();
  const [status, setStatus] = useState<"open" | "all">("open");
  const [reports, setReports] = useState<AdminReport[]>([]);

  async function refresh() {
    try {
      setReports(await hs.adminReports(session.access_token, status === "open" ? "open" : ""));
    } catch (e) {
      message.error(`Could not load reports: ${(e as Error).message}`);
    }
  }
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.access_token, status]);

  async function resolve(id: string, action: "resolve" | "dismiss") {
    try {
      await hs.adminResolveReport(session.access_token, id, action);
      await refresh();
    } catch (e) {
      message.error(`Could not ${action}: ${(e as Error).message}`);
    }
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Segmented size="small" options={[{ label: "Open", value: "open" }, { label: "All", value: "all" }]} value={status} onChange={(v) => setStatus(v as typeof status)} />
      <List
        size="small"
        locale={{ emptyText: <Empty description="No reports" /> }}
        dataSource={reports}
        renderItem={(r) => (
          <List.Item
            actions={[
              ...(onDiscuss && r.reporter !== session.user_id
                ? [
                    <Button key="discuss" size="small" type="link" onClick={() => onDiscuss(r.id, r.reporter, r.discussion_room)}>
                      {r.discussion_room ? "Open discussion" : "Discuss with reporter"}
                    </Button>,
                  ]
                : []),
              ...(r.status === "open"
                ? [
                    <Button key="r" size="small" type="link" onClick={() => resolve(r.id, "resolve")}>
                      Resolve
                    </Button>,
                    <Button key="d" size="small" type="link" onClick={() => resolve(r.id, "dismiss")}>
                      Dismiss
                    </Button>,
                  ]
                : []),
            ]}
          >
            <Space direction="vertical" size={2} style={{ width: "100%" }}>
              <Space wrap>
                <Tag color={r.status === "open" ? "red" : "default"}>{r.status}</Tag>
                <Typography.Text>{r.reason || <Typography.Text type="secondary">(no reason given)</Typography.Text>}</Typography.Text>
              </Space>
              {r.content && (
                <Typography.Paragraph
                  type="secondary"
                  style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word", background: "rgba(255,255,255,0.04)", padding: "4px 8px", borderRadius: 6 }}
                  ellipsis={{ rows: 4, expandable: true, symbol: "more" }}
                >
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {r.target_sender ? `${r.target_sender}: ` : "reported message: "}
                  </Typography.Text>
                  {r.content}
                </Typography.Paragraph>
              )}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                by {r.reporter} · {new Date(r.created_at).toLocaleString()}
                {r.room_id && onOpenRoom && (
                  <>
                    {" · "}
                    <Typography.Link onClick={() => onOpenRoom(r.room_id)}>open room</Typography.Link>
                  </>
                )}
              </Typography.Text>
            </Space>
          </List.Item>
        )}
      />
    </Space>
  );
}

function AccountsTab({ session }: { readonly session: Session }) {
  const { message } = App.useApp();
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);

  async function refresh() {
    try {
      setAccounts(await hs.adminAccounts(session.access_token));
    } catch (e) {
      message.error(`Could not load accounts: ${(e as Error).message}`);
    }
  }
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.access_token]);

  async function toggle(a: AdminAccount) {
    try {
      await hs.adminSetAccountActive(session.access_token, a.user_id, a.deactivated ? "reactivate" : "deactivate");
      await refresh();
    } catch (e) {
      message.error(`Could not update: ${(e as Error).message}`);
    }
  }

  return (
    <Table<AdminAccount>
      size="small"
      rowKey="user_id"
      dataSource={accounts}
      pagination={{ pageSize: 10, hideOnSinglePage: true }}
      columns={[
        {
          title: "User",
          dataIndex: "localpart",
          render: (_v, a) => (
            <Space size={4}>
              <Typography.Text>{a.localpart}</Typography.Text>
              {a.admin && <Tag color="gold">admin</Tag>}
              {a.deactivated && <Tag color="red">deactivated</Tag>}
            </Space>
          ),
        },
        { title: "Invited by", dataIndex: "invited_by", responsive: ["md"], render: (v) => v || "—" },
        { title: "Joined", dataIndex: "created_at", responsive: ["md"], render: (v) => new Date(v).toLocaleDateString() },
        {
          title: "",
          key: "action",
          render: (_v, a) =>
            a.admin ? null : (
              <Popconfirm
                title={a.deactivated ? "Reactivate this account?" : "Deactivate this account?"}
                description={a.deactivated ? undefined : "Their sessions end immediately and they can't sign in."}
                okText={a.deactivated ? "Reactivate" : "Deactivate"}
                okButtonProps={{ danger: !a.deactivated }}
                onConfirm={() => toggle(a)}
              >
                <Button size="small" danger={!a.deactivated} type="text">
                  {a.deactivated ? "Reactivate" : "Deactivate"}
                </Button>
              </Popconfirm>
            ),
        },
      ]}
    />
  );
}

function RoomsTab({ session, onOpenRoom }: { readonly session: Session; readonly onOpenRoom?: (roomID: string) => void }) {
  const { message } = App.useApp();
  const [rooms, setRooms] = useState<AdminRoom[]>([]);

  async function refresh() {
    try {
      setRooms(await hs.adminRooms(session.access_token));
    } catch (e) {
      message.error(`Could not load rooms: ${(e as Error).message}`);
    }
  }
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.access_token]);

  async function purge(roomID: string) {
    try {
      await hs.adminPurgeRoom(session.access_token, roomID);
      message.success("Room deleted");
      await refresh();
    } catch (e) {
      message.error(`Could not delete: ${(e as Error).message}`);
    }
  }

  return (
    <Table<AdminRoom>
      size="small"
      rowKey="room_id"
      dataSource={rooms}
      pagination={{ pageSize: 10, hideOnSinglePage: true }}
      columns={[
        {
          title: "Room",
          key: "name",
          render: (_v, r) => (
            <Typography.Link onClick={() => onOpenRoom?.(r.room_id)}>{r.name || r.alias || r.room_id}</Typography.Link>
          ),
        },
        { title: "Alias", dataIndex: "alias", responsive: ["md"], render: (v) => v || "—" },
        { title: "Members", dataIndex: "members", width: 80 },
        {
          title: "",
          key: "action",
          width: 70,
          render: (_v, r) => (
            <Popconfirm
              title="Delete this room?"
              description={r.members > 0 ? `${r.members} member(s) are still in it — this is permanent.` : "This permanently removes the room and its history."}
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => purge(r.room_id)}
            >
              <Button size="small" danger type="text">
                Delete
              </Button>
            </Popconfirm>
          ),
        },
      ]}
    />
  );
}
